import { BanknotesIcon, ClockIcon, ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { Money } from "../pages/Pricing";
import { subscriptionOf } from "../services/subscriptionService";

/**
 * Under the top bar whenever the subscription needs attention:
 *  - gated: "Your free trial has ended — choose a plan" / "Awaiting payment
 *    confirmation" / "Choose a plan to continue" (danger);
 *  - a paid plan waiting for payment while the current plan / trial still
 *    runs (warning);
 *  - a running trial: "Free trial — 5 days left" (info).
 * Everything comes from /business/me's `business.subscription`; the server
 * decides the status, the gate and the days left.
 */
export default function TrialBanner({ user }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const sub = subscriptionOf(user);

  if (!sub) return null;

  const pending = sub.status === "pending_payment";
  const trialing = sub.days_left != null;
  let tone;
  let text;

  if (sub.gated) {
    tone = "danger";
    text =
      sub.gate_code === "payment_pending"
        ? t("billing.awaitingPayment")
        : sub.gate_code === "plan_required"
          ? t("billing.planRequired")
          : t("billing.trialEnded");
  } else if (sub.setup_mode) {
    // New sign-up on a paid plan: the portal is open to set up; customers
    // see nothing until the payment is confirmed.
    tone = "warning";
    text = t("billing.setupMode");
  } else if (pending) {
    tone = "warning";
    text = t("billing.awaitingPayment");
  } else if (trialing) {
    tone = "accent";
    text =
      sub.days_left > 0 ? t("billing.trialDaysLeft", { count: sub.days_left }) : t("billing.trialEndsToday");
  } else {
    return null;
  }

  // Still trialing while a payment is pending: say both.
  if (pending && trialing && !sub.gated) {
    text = `${text} · ${t("billing.trialDaysLeft", { count: sub.days_left })}`;
  }

  const style = {
    danger: "bg-danger-wash text-danger-text",
    warning: "bg-warning-wash text-warning-text",
    accent: "bg-accent-wash text-accent-text",
  }[tone];
  const Icon = tone === "danger" ? ExclamationTriangleIcon : pending ? BanknotesIcon : ClockIcon;

  return (
    <div
      role={tone === "danger" ? "alert" : "status"}
      className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center text-sm font-semibold ${style}`}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span>{text}</span>
      {sub.setup_mode && sub.pending?.total ? (
        <span>
          {t("billing.amountDue")} <Money amount={sub.pending.total} currency={sub.pending.currency} />
        </span>
      ) : null}
      {pathname !== "/billing" && (
        <Link to="/billing" className="underline underline-offset-2 hover:no-underline">
          {pending ? t("billing.viewRequest") : t("billing.choosePlan")}
        </Link>
      )}
    </div>
  );
}
